import { fetchAll } from '@/lib/api/paged'
import { api } from '@/lib/api/client'

/**
 * Someone who can be given work.
 *
 * A narrow view of the person record on purpose: this exists to fill an
 * assignee picker, and a picker has no business holding an email address or a
 * start date.
 */
export interface Person {
  id: string
  name: string
  color: string | null
  jobTitle: string | null
}

/**
 * The directory answers 404 to a guest rather than 403 — a guest is not told
 * there is a directory to be refused (access-control.md). So a caller must be
 * ready for this to fail and fall back to showing the assignee as text, which
 * is what a guest should see anyway.
 */
export async function fetchPeople(): Promise<Person[]> {
  return fetchAll<Person>('/people')
}

/** The full profile, as opposed to the picker view above. */
export interface PersonProfile extends Person {
  email: string
  kind: string
  isActive: boolean
  startedOn: string | null
  endedOn: string | null
  divisionId: string | null
  division: { id: string; key: string; name: string } | null
  managerId: string | null
  manager: { id: string; name: string } | null
  teams: { id: string; name: string }[]
  openWorkItems?: number
  ledProjectCount?: number
  notificationPreferences?: NotificationPreferences
}

export interface NotificationPreferences {
  dailyBrief: boolean
  mentions: boolean
  assignments: boolean
  weeklyDigest: boolean
  channel: 'email' | 'in_app' | 'none'
}

export interface PersonChanges {
  jobTitle?: string | null
  divisionId?: string | null
  managerId?: string | null
  startedOn?: string | null
  endedOn?: string | null
  /**
   * Non-structural, so the server lets a person change their own without an
   * administrator — unlike everything above, which needs `administer`.
   */
  notificationPreferences?: Partial<NotificationPreferences>
}

export async function fetchPerson(id: string): Promise<PersonProfile> {
  const { data } = await api.get<{ data: PersonProfile }>(`/people/${id}`)

  return data.data
}

export async function updatePerson(id: string, changes: PersonChanges): Promise<PersonProfile> {
  const { data } = await api.patch<{ data: PersonProfile }>(`/people/${id}`, changes)

  return data.data
}

/** Someone leaving. Their records stay; their access does not. */
export async function setPersonActive(id: string, active: boolean): Promise<PersonProfile> {
  const { data } = await api.post<{ data: PersonProfile }>(
    `/people/${id}/${active ? 'reactivate' : 'deactivate'}`,
    {},
  )

  return data.data
}
